import LeaderboardStats from '../LeaderboardStats';
import React from 'react';
import {
  DateSelector,
  LeaderboardFrame,
  LeaderboardPanel,
  formatLeaderboardDate,
  fetchGameStatuses,
  fetchHasPlayedToday,
  hubHeaders,
} from '@precisionpros/game-ui';
import SeaBoard from '../components/SeaBoard';
import GAME_CONFIG from '../config/game';
import { API_URL, HUB_URL } from '../config/environment';

const dateInGameTimezone = () => {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: GAME_CONFIG.timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(new Date());
  const value = Object.fromEntries(parts.map(({ type, value: part }) => [type, part]));
  return `${value.year}-${value.month}-${value.day}`;
};

const shiftDate = (isoDate, days) => {
  const value = new Date(`${isoDate}T12:00:00`);
  value.setDate(value.getDate() + days);
  return `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, '0')}-${String(value.getDate()).padStart(2, '0')}`;
};

const conditionLabels = {
  'clear-skies': '⛵ Clear Skies',
  'rolling-swells': '💨 Rolling Swells',
  'deep-shroud': '🌫️ Deep Shroud',
};

const formatElapsed = (milliseconds) => {
  if (milliseconds == null) return '—';
  const totalSeconds = Math.floor(milliseconds / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  const hundredths = Math.floor((milliseconds % 1000) / 10);
  return `${minutes}:${String(seconds).padStart(2, '0')}.${String(hundredths).padStart(2, '0')}`;
};

function Medal({ rank }) {
  if (rank === 1) return <span aria-label="1st place">🥇</span>;
  if (rank === 2) return <span aria-label="2nd place">🥈</span>;
  if (rank === 3) return <span aria-label="3rd place">🥉</span>;
  return rank;
}

function PuzzleViewDialog({ puzzle, onClose }) {
  if (!puzzle) return null;
  const solvedState = {
    puzzle,
    lighthouses: puzzle.solution.map((column, row) => ({ row, column })),
    previewedLighthouse: null,
    beamFlash: null,
    feedback: null,
  };
  return (
    <div className="sea-puzzle-view-backdrop" onClick={onClose}>
      <section
        className="sea-puzzle-view-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="sea-puzzle-view-title"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="sea-puzzle-view-header">
          <div>
            <h2 id="sea-puzzle-view-title">Puzzle Solution</h2>
            <p>{formatLeaderboardDate(puzzle.date)} · {conditionLabels[puzzle.condition] || puzzle.condition}</p>
          </div>
          <button type="button" className="sea-puzzle-view-close" onClick={onClose} aria-label="Close puzzle solution">×</button>
        </div>
        <SeaBoard state={solvedState} readOnly />
      </section>
    </div>
  );
}

function ResultsTable({ rows, includeStrikes }) {
  if (!rows.length) return <p className="sea-leaderboard-empty">No completed games yet for this day.</p>;
  return (
    <div className="sea-leaderboard-table-wrap">
      <table className="sea-leaderboard-table">
        <thead>
          <tr>
            <th scope="col">Rank</th>
            <th scope="col">Captain</th>
            {includeStrikes && <th scope="col">Strikes</th>}
            <th scope="col">Time</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={`${row.rank}-${row.player_name}`}>
              <td><Medal rank={row.rank} /></td>
              <td className="sea-leaderboard-player">{row.player_name}</td>
              {includeStrikes && <td>{row.strikes}</td>}
              <td className="sea-leaderboard-time">{formatElapsed(row.elapsed_ms)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function LeaderboardPage() {
  const today = React.useMemo(dateInGameTimezone, []);
  const [selectedDate, setSelectedDate] = React.useState(today);
  const [leaderboard, setLeaderboard] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState('');
  const [gameStatuses, setGameStatuses] = React.useState(null);
  const [hasPlayedToday, setHasPlayedToday] = React.useState(false);
  const [puzzleToView, setPuzzleToView] = React.useState(null);
  const [loadingPuzzle, setLoadingPuzzle] = React.useState(false);
  const [puzzleViewError, setPuzzleViewError] = React.useState('');

  React.useEffect(() => {
    fetchGameStatuses().then(setGameStatuses).catch(() => setGameStatuses({}));
  }, []);

  React.useEffect(() => {
    fetchHasPlayedToday(`${API_URL}/has-played-today/`)
      .then((played) => setHasPlayedToday(played === true))
      .catch(() => setHasPlayedToday(false));
  }, []);

  React.useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError('');
    fetch(`${API_URL}/leaderboard/?date=${encodeURIComponent(selectedDate)}`, {
      headers: hubHeaders(),
      signal: controller.signal,
    })
      .then((response) => {
        if (!response.ok) throw new Error(`Leaderboard request failed (${response.status}).`);
        return response.json();
      })
      .then((data) => setLeaderboard(data))
      .catch((requestError) => {
        if (requestError.name !== 'AbortError') {
          setError('We couldn’t load the leaderboard. Please try again shortly.');
          setLeaderboard(null);
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [selectedDate]);

  const pageUrl = window.location.origin;
  const live = window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1';
  const bases = live ? {
    mathpro: 'https://mathpro.precisionpros.com',
    wordpro: 'https://wordpro.precisionpros.com', connectpro: 'https://connectpro.precisionpros.com',
    eventpro: 'https://eventpro.precisionpros.com', buildpro: 'https://buildpro.precisionpros.com',
    wordprofinder: 'https://wordprofinder.precisionpros.com', seashroud: pageUrl,
  } : {
    mathpro: `http://${window.location.hostname}:3008`,
    wordpro: 'http://localhost:3005', connectpro: 'http://localhost:3001', eventpro: 'http://localhost:3002',
    buildpro: 'http://localhost:3003', wordprofinder: 'http://localhost:3004', seashroud: pageUrl,
  };
  const definitions = [
    ['mathpro', 'M', '#996128'], ['wordpro', 'W', '#4CAF50'], ['connectpro', 'C', '#2563eb'], ['eventpro', 'E', '#ea580c'],
    ['buildpro', 'B', '#0f766e'], ['wordprofinder', 'WF', '#4CAF50'], ['seashroud', 'S', GAME_CONFIG.accent],
  ];
  const links = gameStatuses === null ? [] : definitions.map(([key, letter, color]) => ({
    key, letter, color, played: gameStatuses[key] === true,
    href: `${bases[key]}${gameStatuses[key] === true ? '/leaderboard' : '/'}`,
  }));
  const shouldHideToday = selectedDate === today
    && (leaderboard?.hidden === true || hasPlayedToday === false);
  const canViewPuzzle = Boolean(leaderboard?.condition)
    && !shouldHideToday
    && (selectedDate < today || (selectedDate === today && hasPlayedToday));

  const handleViewPuzzle = async () => {
    setLoadingPuzzle(true);
    setPuzzleViewError('');
    try {
      const response = await fetch(`${API_URL}/puzzle/view/?date=${encodeURIComponent(selectedDate)}`, {
        headers: hubHeaders(),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || `Puzzle request failed (${response.status}).`);
      setPuzzleToView(data);
    } catch (requestError) {
      setPuzzleViewError(requestError.message || 'We couldn’t load the puzzle solution.');
    } finally {
      setLoadingPuzzle(false);
    }
  };

  return (
    <LeaderboardFrame
      game={GAME_CONFIG}
      currentGameKey={GAME_CONFIG.key}
      title="Leaderboard"
      links={links}
      homeHref={HUB_URL}
      showPrePlayBanner={shouldHideToday}
      playHref={pageUrl}
      howToPlayHref={`${pageUrl}/how-to-play`}
      dateSelector={(
        <DateSelector
          dateLabel={formatLeaderboardDate(selectedDate)}
          onPrevious={() => setSelectedDate((value) => shiftDate(value, -1))}
          onNext={() => setSelectedDate((value) => shiftDate(value, 1))}
        />
      )}
    >
      <div className="sea-leaderboard-weather-row" aria-live="polite">
        <div className="sea-leaderboard-weather">
          {leaderboard?.condition
            ? `Puzzle condition: ${conditionLabels[leaderboard.condition] || leaderboard.condition}`
            : 'No puzzle has been published for this date.'}
        </div>
        {canViewPuzzle && (
          <button type="button" className="sea-view-puzzle-button" onClick={handleViewPuzzle} disabled={loadingPuzzle}>
            {loadingPuzzle ? 'Loading…' : 'View Puzzle'}
          </button>
        )}
      </div>
      {error && <p className="sea-leaderboard-error" role="alert">{error}</p>}
      {puzzleViewError && <p className="sea-leaderboard-error" role="alert">{puzzleViewError}</p>}
      <h2>Today’s Results</h2>
      {!shouldHideToday && <>
        <LeaderboardPanel subtitle="Zero strikes, ranked by fastest completion time.">
          <div className="sea-leaderboard-heading">
            <span aria-hidden="true">✨</span><h2>Flawless Beacons</h2>
          </div>
          {loading ? <p className="sea-leaderboard-empty" role="status">Loading results…</p> : (
            <ResultsTable rows={leaderboard?.flawless_beacons || []} includeStrikes={false} />
          )}
        </LeaderboardPanel>
        <LeaderboardPanel subtitle="Fewest strikes first, then fastest completion time.">
          <div className="sea-leaderboard-heading">
            <span aria-hidden="true">🧭</span><h2>Navigators</h2>
          </div>
          {loading ? <p className="sea-leaderboard-empty" role="status">Loading results…</p> : (
            <ResultsTable rows={leaderboard?.navigators || []} includeStrikes />
          )}
        </LeaderboardPanel>
      </>}
 <LeaderboardStats endpoint={`${API_URL}/leaderboard-records/`} date={selectedDate} />
      <PuzzleViewDialog puzzle={puzzleToView} onClose={() => setPuzzleToView(null)} />
    </LeaderboardFrame>
  );
}

export default LeaderboardPage;
